import {
  mapTargetPointToCanvas,
  mapViewportPointToCoordinateSpace,
} from './map-viewport'

type TargetPoint = [number, number]
type Segment = { a: TargetPoint; b: TargetPoint }
type ActivePointer = {
  pointerId: number
  clientX: number
  clientY: number
  startTime: number
}

const TRAIL_FADE_ALPHA = 0.05
const TRAIL_IDLE_FADE_FRAMES = 64
const TRAIL_COLOR = { r: 0, g: 255, b: 100 }
const TRAIL_CORE_COLOR = { r: 255, g: 255, b: 255 }

const TRAIL_VISUAL = {
  outerWidth: 18,
  outerBlur: 12,
  outerAlpha: 0.35,
  middleWidth: 10,
  middleBlur: 6,
  middleAlpha: 0.75,
  coreWidth: 3,
  headRadius: 5,
  headGlowRadius: 14,
  headGlowAlpha: 0.9,
  ringCount: 2,
  ringStartRadius: 10,
  ringSpacing: 14,
  ringLineWidth: 2,
  ringPulseSpeed: 1.2,
  ringMaxAlpha: 0.6,
  particleCount: 4,
  particleRadius: 3,
  particleOrbitRadius: 22,
  particleSpeed: 0.06,
  particleAlpha: 0.6,
} as const

export type DragTrackControllerOptions = {
  targetSize: [number, number]
  fitMode?: 'contain' | 'cover'
  mirrored?: boolean
  enabled?: boolean
  onTracks: (tracks: Array<[number, number]>) => void | Promise<void>
  /** Fired once when a real drag stroke begins (first pointer down). */
  onStrokeStart?: () => void | Promise<void>
  /** Fired once when a real drag stroke ends (pointer up/cancel), not on disable/destroy. */
  onStrokeEnd?: () => void | Promise<void>
}

function trailRgba(alpha: number): string {
  return `rgba(${TRAIL_COLOR.r},${TRAIL_COLOR.g},${TRAIL_COLOR.b},${alpha})`
}

function coreRgba(alpha: number): string {
  return `rgba(${TRAIL_CORE_COLOR.r},${TRAIL_CORE_COLOR.g},${TRAIL_CORE_COLOR.b},${alpha})`
}

function fadeTrailCanvas(ctx: CanvasRenderingContext2D) {
  ctx.save()
  ctx.globalCompositeOperation = 'destination-out'
  ctx.fillStyle = `rgba(0,0,0,${TRAIL_FADE_ALPHA})`
  ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height)
  ctx.restore()
}

function drawTrailSegments(
  ctx: CanvasRenderingContext2D,
  segs: Segment[],
  container: HTMLElement,
  targetSize: [number, number],
  dpr: number,
  fitMode: 'contain' | 'cover',
  mirrored: boolean,
  fastMode: boolean,
) {
  if (segs.length === 0) {
    return
  }

  const strokeSegs = (opts: {
    width: number
    shadowBlur: number
    alpha: number
    color: 'trail' | 'core'
  }) => {
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = opts.width * dpr
    ctx.strokeStyle = opts.color === 'core'
      ? coreRgba(opts.alpha)
      : trailRgba(opts.alpha)
    ctx.shadowBlur = fastMode ? 0 : opts.shadowBlur * dpr
    ctx.shadowColor = fastMode || opts.color === 'core'
      ? 'transparent'
      : trailRgba(opts.alpha)
    ctx.beginPath()
    for (const seg of segs) {
      const start = mapTargetPointToCanvas(seg.a, container, targetSize, dpr, { fitMode, mirrored })
      const end = mapTargetPointToCanvas(seg.b, container, targetSize, dpr, { fitMode, mirrored })
      if (!start || !end) {
        continue
      }
      ctx.moveTo(start[0], start[1])
      ctx.lineTo(end[0], end[1])
    }
    ctx.stroke()
    ctx.restore()
  }

  strokeSegs({
    width: TRAIL_VISUAL.outerWidth,
    shadowBlur: TRAIL_VISUAL.outerBlur,
    alpha: TRAIL_VISUAL.outerAlpha,
    color: 'trail',
  })
  strokeSegs({
    width: TRAIL_VISUAL.middleWidth,
    shadowBlur: TRAIL_VISUAL.middleBlur,
    alpha: TRAIL_VISUAL.middleAlpha,
    color: 'trail',
  })
  strokeSegs({
    width: TRAIL_VISUAL.coreWidth,
    shadowBlur: 0,
    alpha: 1,
    color: 'core',
  })
}

function drawHeadGlow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dpr: number,
) {
  const glowRadius = TRAIL_VISUAL.headGlowRadius * dpr
  const headRadius = TRAIL_VISUAL.headRadius * dpr
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, glowRadius)
  gradient.addColorStop(0, trailRgba(TRAIL_VISUAL.headGlowAlpha))
  gradient.addColorStop(0.4, trailRgba(TRAIL_VISUAL.headGlowAlpha * 0.6))
  gradient.addColorStop(1, trailRgba(0))
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(x, y, glowRadius, 0, Math.PI * 2)
  ctx.fill()

  ctx.fillStyle = coreRgba(1)
  ctx.beginPath()
  ctx.arc(x, y, headRadius, 0, Math.PI * 2)
  ctx.fill()
}

function drawPulsingRings(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dpr: number,
  startTime: number,
  now: number,
) {
  const elapsed = (now - startTime) / 1000
  ctx.lineWidth = TRAIL_VISUAL.ringLineWidth * dpr

  for (let index = 0; index < TRAIL_VISUAL.ringCount; index += 1) {
    const baseRadius = (
      TRAIL_VISUAL.ringStartRadius
      + index * TRAIL_VISUAL.ringSpacing
    ) * dpr
    const pulseOffset = elapsed * TRAIL_VISUAL.ringPulseSpeed + index * 0.5
    const pulse = (Math.sin(pulseOffset * Math.PI * 2) + 1) / 2
    const radius = baseRadius + pulse * 6 * dpr
    const alpha = TRAIL_VISUAL.ringMaxAlpha
      * (1 - index * 0.2)
      * (0.5 + pulse * 0.5)

    ctx.strokeStyle = trailRgba(alpha)
    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.stroke()
  }
}

function drawOrbitParticles(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dpr: number,
  startTime: number,
  now: number,
) {
  const elapsed = (now - startTime) / 1000
  const particleRadius = TRAIL_VISUAL.particleRadius * dpr

  for (let index = 0; index < TRAIL_VISUAL.particleCount; index += 1) {
    const baseAngle = (index / TRAIL_VISUAL.particleCount) * Math.PI * 2
    const direction = index % 2 === 0 ? 1 : -1
    const angle = baseAngle + elapsed * TRAIL_VISUAL.particleSpeed * direction
    const orbitRadius = TRAIL_VISUAL.particleOrbitRadius * dpr
    const particleX = x + Math.cos(angle) * orbitRadius
    const particleY = y + Math.sin(angle) * orbitRadius
    const alpha = TRAIL_VISUAL.particleAlpha
      * (0.6 + Math.sin(elapsed * 3 + index) * 0.4)
    const gradient = ctx.createRadialGradient(
      particleX,
      particleY,
      0,
      particleX,
      particleY,
      particleRadius * 2,
    )
    gradient.addColorStop(0, trailRgba(alpha))
    gradient.addColorStop(1, trailRgba(0))
    ctx.fillStyle = gradient
    ctx.beginPath()
    ctx.arc(particleX, particleY, particleRadius * 2, 0, Math.PI * 2)
    ctx.fill()
  }
}

function clearCanvas(ctx: CanvasRenderingContext2D | null): void {
  if (!ctx) {
    return
  }
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
  ctx.restore()
}

export class DragTrackController {
  private readonly container: HTMLElement
  private readonly trailCanvas: HTMLCanvasElement
  private readonly fxCanvas: HTMLCanvasElement
  private readonly onTracks: DragTrackControllerOptions['onTracks']
  private readonly onStrokeStart: DragTrackControllerOptions['onStrokeStart']
  private readonly onStrokeEnd: DragTrackControllerOptions['onStrokeEnd']
  private strokeActive = false
  private hasTrailPixels = false
  private idleFadeFrames = 0
  private targetSize: [number, number]
  private fitMode: 'contain' | 'cover'
  private mirrored: boolean
  private enabled: boolean
  private readonly activePointers = new Map<number, ActivePointer>()
  private readonly lastTargetPoints = new Map<number, TargetPoint>()
  private readonly segQueue: Segment[] = []
  private sampleTimer: number | null = null
  private sampleInFlight = false
  private rafId: number | null = null
  private resizeObserver: ResizeObserver | null = null
  private readonly onWindowResize: () => void
  private readonly onPointerDown: (e: PointerEvent) => void
  private readonly onPointerMove: (e: PointerEvent) => void
  private readonly onPointerUp: (e: PointerEvent) => void
  private readonly onPointerCancel: (e: PointerEvent) => void

  constructor(container: HTMLElement, options: DragTrackControllerOptions) {
    this.container = container
    this.onTracks = options.onTracks
    this.onStrokeStart = options.onStrokeStart
    this.onStrokeEnd = options.onStrokeEnd
    this.targetSize = options.targetSize
    this.fitMode = options.fitMode ?? 'cover'
    this.mirrored = options.mirrored ?? false
    this.enabled = options.enabled ?? true

    this.trailCanvas = document.createElement('canvas')
    this.trailCanvas.className = 'Xmax-drag-track-canvas'
    this.trailCanvas.style.cssText =
      'position:absolute;inset:0;z-index:21;width:100%;height:100%;pointer-events:none;'
    container.appendChild(this.trailCanvas)

    this.fxCanvas = document.createElement('canvas')
    this.fxCanvas.className = 'Xmax-drag-track-fx-canvas'
    this.fxCanvas.style.cssText =
      'position:absolute;inset:0;z-index:22;width:100%;height:100%;pointer-events:none;'
    container.appendChild(this.fxCanvas)

    this.onWindowResize = () => this.resizeCanvas()
    this.onPointerDown = (e) => this.handlePointerDown(e)
    this.onPointerMove = (e) => this.handlePointerMove(e)
    this.onPointerUp = (e) => this.handlePointerUp(e)
    this.onPointerCancel = (e) => this.handlePointerCancel(e)

    this.resizeCanvas()
    this.bindResizeObserver()
    this.bindPointerEvents()
    this.applyInteractionStyle()
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    this.applyInteractionStyle()
    if (!enabled) {
      this.finishStroke()
    }
  }

  setTargetSize(targetSize: [number, number]): void {
    this.targetSize = targetSize
  }

  setFitMode(fitMode: 'contain' | 'cover'): void {
    this.fitMode = fitMode
  }

  setMirrored(mirrored: boolean): void {
    this.mirrored = mirrored
  }

  destroy(): void {
    this.finishStroke()
    this.unbindPointerEvents()
    this.unbindResizeObserver()
    this.trailCanvas.remove()
    this.fxCanvas.remove()
  }

  private applyInteractionStyle(): void {
    this.container.style.pointerEvents = this.enabled ? 'auto' : 'none'
    this.container.style.touchAction = this.enabled ? 'none' : 'auto'
  }

  private bindResizeObserver(): void {
    const ResizeObserverCtor = globalThis.ResizeObserver
    if (ResizeObserverCtor) {
      this.resizeObserver = new ResizeObserverCtor(() => this.resizeCanvas())
      this.resizeObserver.observe(this.container)
      return
    }
    window.addEventListener('resize', this.onWindowResize)
  }

  private unbindResizeObserver(): void {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null
    window.removeEventListener('resize', this.onWindowResize)
  }

  private bindPointerEvents(): void {
    this.container.addEventListener('pointerdown', this.onPointerDown)
    this.container.addEventListener('pointermove', this.onPointerMove)
    this.container.addEventListener('pointerup', this.onPointerUp)
    this.container.addEventListener('pointercancel', this.onPointerCancel)
  }

  private unbindPointerEvents(): void {
    this.container.removeEventListener('pointerdown', this.onPointerDown)
    this.container.removeEventListener('pointermove', this.onPointerMove)
    this.container.removeEventListener('pointerup', this.onPointerUp)
    this.container.removeEventListener('pointercancel', this.onPointerCancel)
    this.activePointers.clear()
    this.lastTargetPoints.clear()
    this.sampleInFlight = false
  }

  private clearVisuals(): void {
    clearCanvas(this.trailCanvas.getContext('2d'))
    clearCanvas(this.fxCanvas.getContext('2d'))
    this.hasTrailPixels = false
    this.idleFadeFrames = 0
  }

  private resizeCanvas(): void {
    const rect = this.container.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    const cssW = Math.max(1, Math.round(rect.width))
    const cssH = Math.max(1, Math.round(rect.height))
    const nextW = Math.max(1, Math.round(cssW * dpr))
    const nextH = Math.max(1, Math.round(cssH * dpr))

    let resized = false
    for (const canvas of [this.trailCanvas, this.fxCanvas]) {
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW
        canvas.height = nextH
        canvas.style.width = `${cssW}px`
        canvas.style.height = `${cssH}px`
        resized = true
      }
    }
    if (resized) {
      this.clearVisuals()
    }
  }

  private toTargetPoint(e: Pick<PointerEvent, 'clientX' | 'clientY'>): TargetPoint | null {
    return mapViewportPointToCoordinateSpace(e, this.container, this.targetSize, {
      fitMode: this.fitMode,
      mirrored: this.mirrored,
    })
  }

  private stopDrawLoop(): void {
    if (this.rafId !== null) {
      window.cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  private renderPointerFx(ctx: CanvasRenderingContext2D, dpr: number): void {
    clearCanvas(ctx)
    if (this.activePointers.size === 0) {
      return
    }

    const now = performance.now()
    const isMultiTouch = this.activePointers.size > 1
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (const pointer of Array.from(this.activePointers.values())) {
      const targetPoint = this.lastTargetPoints.get(pointer.pointerId)
        ?? this.toTargetPoint(pointer)
      if (!targetPoint) {
        continue
      }
      const canvasPoint = mapTargetPointToCanvas(
        targetPoint,
        this.container,
        this.targetSize,
        dpr,
        { fitMode: this.fitMode, mirrored: this.mirrored },
      )
      if (!canvasPoint) {
        continue
      }

      if (!isMultiTouch) {
        drawPulsingRings(
          ctx,
          canvasPoint[0],
          canvasPoint[1],
          dpr,
          pointer.startTime,
          now,
        )
        drawOrbitParticles(
          ctx,
          canvasPoint[0],
          canvasPoint[1],
          dpr,
          pointer.startTime,
          now,
        )
      }
      drawHeadGlow(ctx, canvasPoint[0], canvasPoint[1], dpr)
    }
    ctx.restore()
  }

  private startDrawLoop(): void {
    if (this.rafId !== null) {
      return
    }

    const tick = () => {
      const trailContext = this.trailCanvas.getContext('2d')
      const fxContext = this.fxCanvas.getContext('2d')
      if (!trailContext || !fxContext) {
        this.rafId = null
        return
      }

      const dpr = window.devicePixelRatio || 1
      if (this.hasTrailPixels) {
        fadeTrailCanvas(trailContext)
      }

      const segments = this.segQueue.splice(0)
      if (segments.length > 0) {
        drawTrailSegments(
          trailContext,
          segments,
          this.container,
          this.targetSize,
          dpr,
          this.fitMode,
          this.mirrored,
          this.activePointers.size > 1,
        )
        this.hasTrailPixels = true
        this.idleFadeFrames = 0
      }

      this.renderPointerFx(fxContext, dpr)

      if (this.activePointers.size > 0) {
        this.idleFadeFrames = 0
      } else if (this.hasTrailPixels) {
        this.idleFadeFrames += 1
        if (this.idleFadeFrames > TRAIL_IDLE_FADE_FRAMES) {
          clearCanvas(trailContext)
          this.hasTrailPixels = false
          this.idleFadeFrames = 0
        }
      }

      if (
        this.activePointers.size > 0
        || this.segQueue.length > 0
        || this.hasTrailPixels
      ) {
        this.rafId = window.requestAnimationFrame(tick)
      } else {
        this.rafId = null
      }
    }

    this.rafId = window.requestAnimationFrame(tick)
  }

  private stopSampling(): void {
    if (this.sampleTimer !== null) {
      window.clearInterval(this.sampleTimer)
      this.sampleTimer = null
    }
  }

  private collectCurrentTracks(): Array<[number, number]> {
    const tracks: Array<[number, number]> = []
    for (const pointer of Array.from(this.activePointers.values())) {
      const point = this.toTargetPoint(pointer)
      if (point) {
        tracks.push(point)
      }
    }
    return tracks
  }

  private flushTracks(): void {
    if (this.sampleInFlight) {
      return
    }

    const tracks = this.collectCurrentTracks()
    if (!tracks.length) {
      return
    }

    this.sampleInFlight = true
    Promise.resolve(this.onTracks(tracks))
      .catch(() => {})
      .finally(() => {
        this.sampleInFlight = false
      })
  }

  private startSampling(): void {
    if (this.sampleTimer !== null) {
      return
    }
    this.flushTracks()
    this.sampleTimer = window.setInterval(() => {
      this.flushTracks()
    }, 30)
  }

  private finishStroke(emitEnd = false, preserveTrail = false): void {
    const wasActive = this.strokeActive
    this.strokeActive = false
    this.stopSampling()
    clearCanvas(this.fxCanvas.getContext('2d'))

    if (preserveTrail) {
      if (this.segQueue.length > 0 || this.hasTrailPixels) {
        this.startDrawLoop()
      } else {
        this.stopDrawLoop()
      }
    } else {
      this.stopDrawLoop()
      this.segQueue.length = 0
      this.activePointers.clear()
      this.lastTargetPoints.clear()
      this.clearVisuals()
    }

    if (emitEnd && wasActive && this.onStrokeEnd) {
      Promise.resolve(this.onStrokeEnd()).catch(() => {})
    }
  }

  private handlePointerDown(e: PointerEvent): void {
    if (!this.enabled) {
      return
    }
    try {
      this.container.setPointerCapture(e.pointerId)
    } catch {
    }
    this.resizeCanvas()
    if (this.activePointers.size === 0) {
      this.idleFadeFrames = 0
    }
    this.activePointers.set(e.pointerId, {
      pointerId: e.pointerId,
      clientX: e.clientX,
      clientY: e.clientY,
      startTime: performance.now(),
    })
    const targetPoint = this.toTargetPoint(e)
    if (targetPoint) {
      this.lastTargetPoints.set(e.pointerId, targetPoint)
    }
    const strokeWasActive = this.strokeActive
    this.strokeActive = true
    if (!strokeWasActive && this.onStrokeStart) {
      Promise.resolve(this.onStrokeStart()).catch(() => {})
    }
    this.startDrawLoop()
    this.startSampling()
  }

  private handlePointerMove(e: PointerEvent): void {
    if (!this.enabled || !this.activePointers.has(e.pointerId)) {
      return
    }
    const activePointer = this.activePointers.get(e.pointerId)
    const nextTargetPoint = this.toTargetPoint(e)
    const last = this.lastTargetPoints.get(e.pointerId)
    if (last && nextTargetPoint) {
      this.segQueue.push({ a: last, b: nextTargetPoint })
    }
    if (nextTargetPoint) {
      this.lastTargetPoints.set(e.pointerId, nextTargetPoint)
    }
    this.activePointers.set(e.pointerId, {
      pointerId: e.pointerId,
      clientX: e.clientX,
      clientY: e.clientY,
      startTime: activePointer?.startTime ?? performance.now(),
    })
  }

  private handlePointerUp(e: PointerEvent): void {
    if (!this.activePointers.has(e.pointerId)) {
      return
    }
    this.activePointers.delete(e.pointerId)
    this.lastTargetPoints.delete(e.pointerId)
    try {
      this.container.releasePointerCapture(e.pointerId)
    } catch {
    }
    if (this.activePointers.size === 0) {
      this.finishStroke(true, true)
    }
  }

  private handlePointerCancel(e: PointerEvent): void {
    if (!this.activePointers.has(e.pointerId)) {
      return
    }
    this.activePointers.delete(e.pointerId)
    this.lastTargetPoints.delete(e.pointerId)
    if (this.activePointers.size === 0) {
      this.finishStroke(true, true)
    }
  }
}

export function createDragTrackController(
  container: HTMLElement,
  options: DragTrackControllerOptions,
): DragTrackController {
  return new DragTrackController(container, options)
}
